import { useEffect, useRef } from 'react';

interface HeadProps {
  title?: string;
  children?: React.ReactNode;
}

export default function Head({ title, children }: HeadProps) {
  const prevTitle = useRef('');

  useEffect(() => {
    prevTitle.current = document.title;
    if (title) {
      document.title = title;
    }
    return () => {
      document.title = prevTitle.current;
    };
  }, [title]);

  return <>{children}</>;
}

export function PageMeta({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  ogType,
  publishedTime,
  author,
  tags,
}: {
  title: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  ogType?: string;
  publishedTime?: string;
  author?: string;
  tags?: string[];
}) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const created: HTMLElement[] = [];

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute('content', content);
    };

    if (description) setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    if (ogTitle) setMeta('og:title', ogTitle, true);
    if (ogDescription) setMeta('og:description', ogDescription, true);
    if (ogImage) setMeta('og:image', ogImage, true);
    if (ogUrl) setMeta('og:url', ogUrl, true);
    if (ogType) setMeta('og:type', ogType, true);
    if (publishedTime) setMeta('article:published_time', publishedTime, true);
    if (author) setMeta('article:author', author, true);
    if (tags) {
      tags.forEach((tag) => {
        const el = document.createElement('meta');
        el.setAttribute('property', 'article:tag');
        el.setAttribute('content', tag);
        document.head.appendChild(el);
        created.push(el);
      });
    }

    const removeCanonical = () => {
      document.querySelectorAll('link[rel="canonical"]').forEach((el) => el.remove());
    };

    if (canonical) {
      removeCanonical();
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
        created.push(link);
      }
      link.href = canonical;
    }

    return () => {
      document.title = prevTitle;
      created.forEach((el) => el.remove());
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogImage, ogUrl, ogType, publishedTime, author, tags]);

  return null;
}

export function JsonLd({ data }: { data: Record<string, unknown> }) {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
    return () => {
      script.remove();
    };
  }, [data]);

  return null;
}